import AFRAME from '@webdev/aframe';
import React, { useCallback, useEffect, useRef } from 'react';
import type { ACameraElement, AframeOptions } from './AframeTypes';
import { AHoverCircleProps, AHoverTextProps } from './AframeTypes';

AFRAME.registerComponent('face-to-camera', {
  getCameraRotationPosition: function (): { x?: string; y?: string; z?: string } {
    const cameraEl = this.el.sceneEl?.camera.el;
    return cameraEl?.getAttribute('rotation') ?? {};
  },

  tick: function () {
    const { x, y, z } = this.getCameraRotationPosition();
    this.el.setAttribute('rotation', `${x} ${y} ${z}`);
  },
});

const defaultBackgroundImageProps: any = {
  material: 'color: black',
};

const convertAframeOptions = ({ faceToCamera, position }: AframeOptions) => ({
  ...(faceToCamera && { 'face-to-camera': '' }),
  ...(position && { position: `${position.x} ${position.y} ${position.z}` }),
});

const useEvent = <K extends keyof HTMLElementEventMap, E extends HTMLElement = HTMLElement>(
  elementRef: React.RefObject<E>,
  eventType: K,
  callback?: (ev: HTMLElementEventMap[K]) => any,
) => {
  useEffect(() => {
    const element = elementRef.current;
    if (!element || !callback) {
      return;
    }
    element.addEventListener(eventType, callback);
    return () => {
      element.removeEventListener(eventType, callback);
    };
  }, [elementRef, eventType, callback]);
};

export const AHoverCircle: React.FC<AHoverCircleProps> = ({ onClick, Node, ...otherProps }) => {
  const circleRef = useRef<HTMLElement>(null);
  const ringRef = useRef<HTMLElement>(null);

  const onMouseenter = useCallback(() => {
    ringRef.current?.setAttribute(
      'animation',
      'property: scale; from: 1 1 1; to: 0.7 0.7 0.7; dur: 1000; easing: easeInOutCubic; loop:true; enabled:true; dir:alternate;',
    );
    ringRef.current?.setAttribute(
      'animation__2',
      'property: geometry.radiusInner; from: 0.21; to: 0.16; dur: 1000; easing: easeInOutCubic; loop:true; enabled:true; dir:alternate;',
    );
  }, [ringRef]);

  const onMouseleave = useCallback(() => {
    ringRef.current?.setAttribute(
      'animation',
      'property: scale; to:  1 1 1; dur: 200; easing: linear; loop:false',
    );
    ringRef.current?.setAttribute(
      'animation__2',
      'property: geometry.radiusInner; to: 0.21; dur: 200; easing: linear; loop:false',
    );
  }, [ringRef]);

  useEvent(circleRef, 'mouseenter', onMouseenter);
  useEvent(circleRef, 'mouseleave', onMouseleave);

  useEvent(circleRef, 'click', onClick);

  return (
    <a-entity {...convertAframeOptions(otherProps)}>
      <a-entity
        ref={ringRef}
        geometry='primitive: ring; radiusInner: 0.21; radiusOuter: 0.24;'
        material='color: white'
      />
      <a-entity
        ref={circleRef}
        geometry='primitive: circle; radius:0.24;'
        material='opacity: 0.0; transparent: true"'
      />
      {Node && <Node onClick={onClick} onMouseenter={onMouseenter} onMouseleave={onMouseleave} />}
    </a-entity>
  );
};

export const AImage: React.FC<React.ImgHTMLAttributes<HTMLImageElement>> = (props) => (
  <img {...props} alt='' {...defaultBackgroundImageProps} crossOrigin='anonymous' />
);

export const ASky: React.FC<{ src: string }> = ({ src }) => {
  const segments = AFRAME.utils.device.isMobile() ? 64 : 128;
  return <a-sky segments-height={segments} segments-width={segments} radius='100' src={src} />;
};

export const AScene: React.FC = ({ children }) => (
  <a-scene
    device-orientation-permission-ui='enabled: true'
    vr-mode-ui='enabled:false'
    loading-screen='backgroundColor: black'
    cursor='rayOrigin: mouse'
  >
    {children}
  </a-scene>
);

export const AAssets: React.FC = ({ children }) => <a-assets> {children}</a-assets>;

export const ACamera = React.forwardRef<any, React.PropsWithChildren<{ position: string }>>(
  ({ position, children }, ref) => (
    <a-camera ref={ref} position={position} reverse-mouse-drag='true'>
      {children}
    </a-camera>
  ),
);

export const ACursor: React.FC = () => (
  <a-cursor
    id='cursor'
    scale='0.5 0.5 1'
    color='#ef9604'
    visible='false'
    raycaster='objects: .link'
  />
);

export const resetCameraRotation = ({ components }: Pick<ACameraElement, 'components'>) => {
  const lookControls = components['look-controls'];
  if (!lookControls || !lookControls.yawObject || !lookControls.pitchObject) {
    return;
  }
  lookControls.yawObject.rotation.y = 0;
  lookControls.pitchObject.rotation.x = 0;
};

export const AHoverText: React.FC<AHoverTextProps> = ({
  text,
  width,
  height,
  onMouseleave,
  onClick,
  onMouseenter,
  ...otherProps
}) => {
  const textRef = useRef<HTMLElement>(null);
  useEvent(textRef, 'mouseenter', onMouseenter);
  useEvent(textRef, 'mouseleave', onMouseleave);
  useEvent(textRef, 'click', onClick);

  return (
    <a-entity
      ref={textRef}
      geometry={`primitive: plane; width: ${width}; height:${height}`}
      material='opacity: 0.0; transparent: true;'
      {...convertAframeOptions(otherProps)}
    >
      <a-entity text={`value: ${text}; color: white; align: center;`} scale='3.5 3.5 3.5' />
    </a-entity>
  );
};
